import React, { useState, useEffect, useRef, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterClass,
  CharacterFeatureBase,
  CharacterFeatureSource,
  SubclassFeature,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterFeatureBaseInput from "@dndComponents/character/features/CharacterFeatureBaseInput";

interface Props {
  character: Character;
  source: CharacterFeatureSource;
  log_it?: boolean;
  onCFBSelect?: (selected: CharacterFeatureBase | undefined) => void;
  expanded_cfb_id: string;
  show_cfb_levels?: boolean;
}

// const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function CharacterFeatureBasesInput({
  character,
  source,
  log_it = false,
  onCFBSelect,
  expanded_cfb_id,
  show_cfb_levels = false
}: Props) {
  const [reloading, setReloading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
  }, [character, mounted, recalculated]);

  const getCharFeatureBasesForRender = (char_feature_bases: CharacterFeatureBase[]) => {
    const use_char_feature_bases = char_feature_bases.filter(o => o.feature_base &&
      (o.feature_base.required_condition_ids.length === 0 || 
        char_util.hasCondition(character, o.feature_base.required_condition_ids, o.feature_base.condition_level)) && 
      (!o.replace_key || o.replaces_cfb_on_source_id !== "")
    );
    const cfbs_by_level: { [sort_level: number]: { [source_true_id: string]: { cfs: CharacterFeatureSource, cfbs: CharacterFeatureBase[]} } } = {};
    // if (source instanceof CharacterClass && 
    //   source.subclass && source.subclass.use_subclass_levels) {
    //   // debugger;
    //   // We're using subclass levels, 
    //   // so we need to figure out where to put the subclass features 
    //   // differently from other features.
    //   const subclass_cfs = source.sub_char_feature_sources.find(o => o.feature_source_type === "Subclass");
    //   if (subclass_cfs) {
    //     const subclass_id = subclass_cfs.source_id;
    //     const getSortLevel = (cfb: CharacterFeatureBase) => {
    //       if (cfb.source_id === subclass_id) {
    //         const subclass_level_cfb = use_char_feature_bases.find(o => o.features.find(o2 => o2.floating_feature instanceof SubclassFeature && o2.floating_feature.level === cfb.level));
    //         if (subclass_level_cfb) {
    //           return +subclass_level_cfb.sort_level;
    //         }
    //         return +cfb.sort_level;
    //       } else {
    //         return +cfb.sort_level;
    //       }
    //     }
    //     for (const cfb of use_char_feature_bases.filter(o => o.display)) {
    //       const sort_level = getSortLevel(cfb);
    //       const cfs = char_util.getCFSForCF(character, cfb);
    //       if (cfs) {
    //         if (!cfbs_by_level[sort_level]) {
    //           cfbs_by_level[sort_level] = {};
    //         }
    //         if (!cfbs_by_level[sort_level][cfs.true_id]) {
    //           cfbs_by_level[sort_level][cfs.true_id] = {
    //             cfs,
    //             cfbs: []
    //           };
    //         }
    //         cfbs_by_level[sort_level][cfs.true_id].cfbs.push(cfb);
    //       }
    //     }
    //     return cfbs_by_level;
    //   }
    // }
    for (const cfb of use_char_feature_bases.filter(o => o.display)) {
      // if (cfb.name.includes("Reaper")) {
      //   // debugger;
      //   console.log(cfb.sort_level);
      // }
      const sort_level = cfb.sort_level;
      const cfs = char_util.getCFSForCF(character, cfb);
      if (cfs) {
        if (!cfbs_by_level[sort_level]) {
          cfbs_by_level[sort_level] = {};
        }
        if (!cfbs_by_level[sort_level][cfs.true_id]) {
          cfbs_by_level[sort_level][cfs.true_id] = {
            cfs,
            cfbs: []
          };
        }
        cfbs_by_level[sort_level][cfs.true_id].cfbs.push(cfb);
      }
    }
    return cfbs_by_level;
  }

  const combined_features = char_util.useFeatures2(character, source, true); // , log_it
  const char_feature_bases = combined_features.sort((a, b) => (a.feature_base && b.feature_base && a.feature_base.level > b.feature_base.level) ? 1 : -1);
  const cfbs_by_level = getCharFeatureBasesForRender(char_feature_bases);

  const renderSortLevel = (sort_level: number) => {
    const cfbs_by_source = cfbs_by_level[sort_level];
    const source_true_ids = Object.keys(cfbs_by_source);
    // console.log(cfbs_by_level);
    return (
      <Grid size={12} key={sort_level}>
        <Grid container spacing={0} direction="row">
          { show_cfb_levels &&
            <Grid size={12} className="header gray">Level { sort_level }</Grid>
          }
          { source_true_ids.sort((a, b) => {
            const cfsa = cfbs_by_source[a].cfs;
            const cfsb = cfbs_by_source[b].cfs;
            if (cfsa.ancestries.length === cfsb.ancestries.length) {
              return cfsa.name.localeCompare(cfsb.name);
            } else {
              return (+cfsa.ancestries.length < +cfsb.ancestries.length ? -1 : 1);
            }
          }).map(source_true_id => renderSourceTrueId(cfbs_by_source, source_true_id))}
        </Grid>
      </Grid>
    );
  }

  const renderSourceTrueId = (cfbs_by_source: { [source_true_id: string]: { cfs: CharacterFeatureSource, cfbs: CharacterFeatureBase[]} }, source_true_id: string) => {
    const entry = cfbs_by_source[source_true_id];
    return (
      <Grid size={12} key={source_true_id}>
        <Grid container spacing={0} direction="row">
          { source && source.true_id !== source_true_id && 
            <Grid
              className="header gray"
              size={{
                xs: 12
              }}>{ entry.cfs.name }</Grid>  
          }
          { entry.cfbs.sort((a, b) => {
            if (a.position_at_level !== b.position_at_level) {
              return a.position_at_level - b.position_at_level;
            } else {
              return a.name.localeCompare(b.name);
            }
          }).map(cfb => (
            <Grid key={cfb.true_id} size={12}>
              <CharacterFeatureBaseInput
                char={character}
                source={entry.cfs}
                obj={cfb}
                onSelect={() => {
                  if (onCFBSelect) {
                    onCFBSelect(cfb);
                  }
                }}  
                selected={cfb.true_id === expanded_cfb_id}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  }
  
  // console.log("Rendering");
  if (log_it) {
    console.log(source);
    console.log(char_feature_bases);
    console.log(char_feature_bases.map(o => o.level));
    console.log(cfbs_by_level);
  }
  const sort_levels = Object.keys(cfbs_by_level).map(o => +o); // .sort();
  return (
    <Grid container spacing={0} direction="row">
      { sort_levels.map(sort_level => renderSortLevel(sort_level))}
    </Grid>
  );
}
